import { useEffect, useMemo, useState } from "react";
import { v4 as uuid } from "uuid";
import { styled } from "@mui/material/styles";
import { Link, useTheme, useMediaQuery } from "@mui/material";
import { useVrsTranslationState } from "../../../context/AppContext/AppContext";
import { SEARCH_RESULT_PAGE_SIZE } from "../../../constants/global";
import { mySecondaryColor } from "../../../constants/colors";
import { GridColDef } from "@mui/x-data-grid-pro";
import { StyledHiddenColumnMenu } from "../../StyledDataGrid/StyledHiddenColumnMenu";
import { EnhancedDataGrid } from "../../../components/StyledDataGrid/EnhancedDataGrid";

// Custom styled link similar to your existing StyledLink
const StyledLink = styled(Link)({
  textDecoration: "underline",
  cursor: "pointer",
  "@media (hover: hover)": {
    "&:hover": {
      color: mySecondaryColor,
    },
  },
});

interface IPlantSearchResultTableProps {
  plants: Array<any>;
  onPlantSearchSelectionChanged: (plant: any) => void;
}

const PlantSearchResultTable = ({ plants, onPlantSearchSelectionChanged }: IPlantSearchResultTableProps) => {
  const { _T } = useVrsTranslationState();
  const theme = useTheme();
  const isXsScreen = useMediaQuery(theme.breakpoints.only('xs'));
  const tableHeadAlign = "center";

  // Local state for rows; if you need to filter/sort, do that here.
  const [rows, setRows] = useState<any>([]);

  useEffect(() => {
    const rowData = plants.map((plant) => ({ id: uuid(), ...plant }));
    setRows(rowData);
  }, [plants]);

  // Responsive minWidth based on screen size
  const columnMinWidth = isXsScreen ? 150 : 200;

  const columns: GridColDef[] = useMemo(() => [
    {
      field: "companyId",
      headerName: _T("Company"),
      flex: 1,
      minWidth: columnMinWidth,
      headerAlign: tableHeadAlign,
      align: "center",
    },
    {
      field: "companyName",
      headerName: _T("Company Name"),
      flex: 1,
      minWidth: columnMinWidth,
      headerAlign: tableHeadAlign,
      align: "center",
    },
    {
      field: "plantId",
      headerName: _T("Site"),
      flex: 1,
      minWidth: columnMinWidth,
      headerAlign: tableHeadAlign,
      align: "center",
    },
    {
      field: "plantName",
      headerName: _T("Site_Name"),
      flex: 1,
      minWidth: columnMinWidth,
      headerAlign: tableHeadAlign,
      align: "center",
      renderCell: (params) => (
        <StyledLink onClick={() => onPlantSearchSelectionChanged(params.row)}>
          <strong>{params.value}</strong>
        </StyledLink>
      ),
    },
    {
      field: "shipToSiteId",
      headerName: _T("Ship-to-Site Id"),
      flex: 1,
      minWidth: columnMinWidth,
      headerAlign: tableHeadAlign,
      align: "center",
    }
  ], [onPlantSearchSelectionChanged, _T, tableHeadAlign, columnMinWidth]);

  return (
    <EnhancedDataGrid
      sx={theme => ({
        "& .MuiDataGrid-columnHeaders": {
          backgroundColor: "background.paper",
        },
        '& .MuiDataGrid-row:hover': {
          backgroundColor: theme.palette.action.hover,
        },
        '& .MuiTablePagination-root .MuiTablePagination-input': {
          display: 'flex',
          maxWidth: '50px !important',
        }
      })}
      initialState={{
        sorting: {
          sortModel: [{ field: 'eventName', sort: 'asc' }],
        },
        pagination: {
          paginationModel: {
            pageSize: SEARCH_RESULT_PAGE_SIZE,
          },
        },
      }}
      pageSizeOptions={[SEARCH_RESULT_PAGE_SIZE, 50, 100]}
      hideFooterPagination={rows?.length <= SEARCH_RESULT_PAGE_SIZE}
      rows={rows}
      columns={columns}
      slots={{
        columnMenu: StyledHiddenColumnMenu,
      }}
      disableRowSelectionOnClick
    />
  );
};

export default PlantSearchResultTable;
